import type {
	GraphModification,
	InstanceDataobject,
	Graph,
	ConceptDataobject,
	InstanceAttribute,
	KnowledgeObject,
} from "@lr-notitia/notitia-operator/schemas";
import { Ref, computed } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import { handleGraphModification } from "./utils";
import { useDomainStore } from "../domain";
import { useGraphStore } from "../graph";
import { useInstanceWorkspacesStore } from "../i-workspace";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let instanceWorkspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	instanceWorkspacesStore = useInstanceWorkspacesStore();
}

export const instanceDataobjects = (graph: Ref<Graph>) =>
	computed<InstanceDataobject[]>(
		() =>
			Object.values(graph.value.nodes).filter(
				(node: any) => node.type === "dataobject" && node.layer === "instance",
			) as unknown as InstanceDataobject[],
	);
export function instanceDataobject(domainId: Ref<string>, graph: Ref<Graph>) {
	return {
		byId: (id: string) => byId(id, graph),
		add: (conceptId: string, templateId: string, parentId: string | undefined) =>
			add(domainId, graph, conceptId, templateId, parentId),
		remove: (id: string) => remove(domainId, graph, id),
		composedDataobjects: (id: string) => composedDataobjects(id, graph),
		composedDataobjectsIds: (id: string) => composedDataobjectsIds(id, graph),
	};
}

function byId(id: string, graph: Ref<Graph>): InstanceDataobject | undefined {
	let hit = graph.value.nodes[id] as KnowledgeObject;
	return hit && hit.type === "dataobject" && hit.layer === "instance" ? (hit as InstanceDataobject) : undefined;
}

function add(
	domainId: Ref<string>,
	graph: Ref<Graph>,
	conceptId: string,
	templateId: string,
	parentId: string | undefined,
): Promise<GraphModification> {
	if (!graphStore || !domainStore || !instanceWorkspacesStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!instanceWorkspacesStore.openedWorkspaceId) return reject();
		if (instanceWorkspacesStore.isOpenedWorkspaceLocked) return reject();
		api()
			.domain(domainId.value)
			.workspace(instanceWorkspacesStore.openedWorkspaceId)
			.dataobjects()
			.post({ conceptId, templateId, parent: parentId })
			.then((data) => {
				handleGraphModification(data.modification, data.metadata);
				resolve(data.modification);
			})
			.catch(() => reject());
	});
}

function remove(domainId: Ref<string>, graph: Ref<Graph>, id: string): Promise<GraphModification> {
	if (!domainStore || !instanceWorkspacesStore) init();
	return new Promise((resolve, reject) => {
		if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
		if (!graph.value.nodes[id]) return reject();
		if (!instanceWorkspacesStore.openedWorkspaceId) return reject();
		if (instanceWorkspacesStore.isOpenedWorkspaceLocked) return reject();
		api()
			.domain(domainId.value)
			.workspace(instanceWorkspacesStore.openedWorkspaceId)
			.dataobject(id)
			.del()
			.then((data) => {
				handleGraphModification(data);
				if (instanceWorkspacesStore.openedWorkspaceId)
					delete instanceWorkspacesStore.nodePositions[instanceWorkspacesStore.openedWorkspaceId]?.[id];
				resolve(data);
			})
			.catch(() => reject());
	});
}

export type ComposedDataobject = {
	id: string;
	dataobjects: ComposedDataobject[];
	attributes: string[];
};
function composedDataobjects(id: string, graph: Ref<Graph>): ComposedDataobject | undefined {
	let result: ComposedDataobject = {
		id: id,
		dataobjects: [],
		attributes: attributesOfDataobject(id, graph),
	};
	const dataobject = graph.value.nodes[id] as unknown as InstanceDataobject;
	if (!dataobject) return;
	const dataobjectConcept = graph.value.nodes[dataobject.concept] as ConceptDataobject;
	if (!dataobjectConcept) return;
	dataobject.composedBy.forEach((composedById) => {
		let composed = composedDataobjects(composedById, graph);
		if (composed) {
			result.dataobjects.push(composed);
		}
	});
	return result;
}
function attributesOfDataobject(id: string, graph: Ref<Graph>): string[] {
	let result: string[] = [];
	let all = Object.values(graph.value.nodes).filter(
		(node: any) => node.type === "attribute" && node.layer === "instance",
	) as unknown as InstanceAttribute[];
	all.forEach((attr) => {
		if (attr?.partOf === id) result.push(attr.id);
	});
	return result;
}
function composedDataobjectsIds(id: string, graph: Ref<Graph>): string[] {
	let result = [id];
	const dataobject = graph.value.nodes[id] as unknown as InstanceDataobject;
	if (!dataobject) return result;
	dataobject.composedBy.forEach((composedById) => {
		result = result.concat(composedDataobjectsIds(composedById, graph));
	});
	return result;
}
